import {
  formatHoursMinutesSeconds,
  imageResizer,
  buildPromoImageProps
} from "../../../lib/utils";
import { AutoPlayStatus, DEFAULT_IMAGE } from "../../../globals";
import { autoplayStateAllowsAds, videoHasAds } from "../../ads/utils/helpers";
import { isLiveEventUpcoming } from "../../utils/liveVideoUtils";
import { getIsVertical } from "../../utils";

// Check whether animated promo is enabled and permissable
export const getAnimatedPromo = video => {
  const readerWantsReducedMotion =
    typeof window !== "undefined"
      ? window.matchMedia(`(prefers-reduced-motion: reduce)`) === true ||
        window.matchMedia(`(prefers-reduced-motion: reduce)`).matches === true
      : false;
  return (
    !readerWantsReducedMotion && video?.additional_properties?.gifAsThumbnail
  );
};

export const getBackgroundVideo = video => {
  const animatedPromo = getAnimatedPromo(video);
  const mp4Promo = animatedPromo
    ? (video?.streams || []).filter(
        stream => stream?.stream_type === "gif-mp4"
      )[0]
    : null;

  return video?.bandito?.video || mp4Promo?.url;
};

export const getPromoImageSource = (video, placeholder) => {
  const animatedPromo = getAnimatedPromo(video);
  const gifPromo = animatedPromo
    ? (video?.streams || []).filter(stream => stream?.stream_type === "gif")[0]
    : null;

  const usePlaceholder =
    placeholder?.image &&
    !(video?.bandito?.image || gifPromo?.url || video?.promo_image?.url);
  if (usePlaceholder) {
    return placeholder.image;
  }

  return (
    video?.bandito?.image ||
    gifPromo?.url ||
    video?.promo_image?.url ||
    DEFAULT_IMAGE
  );
};

export const getVideoImage = (video, placeholder) => {
  const isVertical = video?.additional_properties?.vertical;
  const width = isVertical ? 480 : 960;

  const animatedPromo = getAnimatedPromo(video);
  const gifPromo = animatedPromo
    ? (video?.streams || []).filter(stream => stream?.stream_type === "gif")[0]
    : null;
  const usePlaceholder =
    placeholder?.image &&
    !(video?.bandito?.image || gifPromo?.url || video?.promo_image?.url);

  if (usePlaceholder && placeholder?.resize === false) {
    return placeholder.image;
  }

  return imageResizer(getPromoImageSource(video, placeholder), width);
};

export const getBackgroundImage = (
  videoData,
  placeholder,
  alternateArt,
  isPlaythrough,
  aspectRatio
) => {
  const isVertical = videoData?.additional_properties?.vertical;
  const width = isVertical ? 480 : 960;

  if (alternateArt && !isPlaythrough) {
    return imageResizer(alternateArt, width, width * aspectRatio);
  } else {
    // Set background image.
    return getVideoImage(videoData, placeholder);
  }
};

// responsive controls the new srcset path; resize applies to the legacy path only
export const getPromoBackgroundImageProps = (
  videoData,
  placeholder,
  alternateArt,
  isPlaythrough,
  aspectRatio,
  buildPromoImagePropsOverride
) => {
  if (placeholder?.responsive !== true) return null;

  const isVertical = getIsVertical(videoData);

  let baseUrl;
  let height = null;

  if (alternateArt && !isPlaythrough) {
    baseUrl = alternateArt;
    const fallbackWidth = isVertical ? 480 : 960;
    height = fallbackWidth * aspectRatio;
  } else {
    baseUrl = getPromoImageSource(videoData, placeholder);
  }

  if (!baseUrl) return null;

  return typeof buildPromoImagePropsOverride === "function"
    ? buildPromoImagePropsOverride(baseUrl, isVertical, height)
    : buildPromoImageProps(baseUrl, isVertical, height);
};

export const getPromoTitle = videoData => {
  return videoData?.bandito?.title || videoData?.headlines?.basic;
};

export const getShouldRenderPromo = (
  autoplayState,
  shouldShowAds,
  videoData,
  config,
  isPromoEnabled,
  isVideoApiDataFailed
) => {
  if (!autoplayState.isInitialized) return false;

  const autoplayStateAllowsPromo =
    !autoplayState.isAutoplay ||
    autoplayState.status === AutoPlayStatus.COMPLETE ||
    autoplayState.isLivePreview;

  const hasAds =
    shouldShowAds &&
    autoplayStateAllowsAds(autoplayState) &&
    videoHasAds(videoData, config);

  return (
    isPromoEnabled &&
    !isVideoApiDataFailed &&
    (autoplayStateAllowsPromo || hasAds || isLiveEventUpcoming(videoData))
  );
};

export const getPromoButtonAriaLabel = (isVideoTypeLive, title, duration) => {
  return `Play ${isVideoTypeLive ? "Live " : ""}video: ${title}, ${
    duration.srOnly
  }`;
};

export const shouldRenderTitle = (
  showBtnText,
  isEnded,
  burnedHeadline,
  customBtnText
) => {
  return (
    showBtnText &&
    !isEnded &&
    // Don't show headline if it's burned in, unless customBtnText is provided
    (!burnedHeadline || !!customBtnText)
  );
};

export const getPromoTextAriaLabel = (isVideoTypeLive, title) => {
  return `Upcoming ${isVideoTypeLive ? "Live " : ""}video: ${title}`;
};

const DEFAULT_LIVE_PREVIEW_TEXT = "Tap to unmute";

export const getPromoText = (
  isForceLoadVideo,
  title,
  customBtnText,
  isLivePreviewEnabled,
  isUpcoming
) => {
  if (isForceLoadVideo) return title;
  if (customBtnText) return customBtnText;
  if (isLivePreviewEnabled && !isUpcoming) return DEFAULT_LIVE_PREVIEW_TEXT;
  return title;
};

export const getBackgroundImageCSS = (
  backgroundImage,
  aspectMatch,
  aspectRatio
) => {
  return {
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    backgroundImage: backgroundImage ? `url(${backgroundImage})` : "none",
    backgroundSize: aspectMatch ? "cover" : "contain",
    ["--aspect-height"]: aspectRatio,
    ["--aspect-width"]: 1
  };
};

export const getPromoDuration = videoData => {
  return formatHoursMinutesSeconds(videoData?.duration);
};
